import { 
	LineChart, 
	Brain, 
	Code, 
	ClipboardList,
	Search,
	Workflow,
	Calendar,
	Target,
	Globe,
	Layers,
	Zap,
	Bot,
	LayoutDashboard,
	Database,
	Shield,
	GraduationCap,
	Lightbulb,
	BookOpen,
	FileCheck,
	Activity
} from 'lucide-react';
import { Service } from '@/types/service.types';

export const services: Service[] = [
	{
		id: 'design',
		title: 'Evaluate & Design',
		description:
			"Great solutions start with great questions. In this phase, we assess your current state, define your ideal future, and map the best path forward. Whether you're launching a project, redesigning operations, or planning a digital transformation, we offer strategic support tailored to your goals. These services are the foundation of everything else we build.",
		icon: Brain,
		gradient: 'from-blue-500 to-indigo-500',
		image: '/images/services/strategy.png',
		features: [
			{
				name: 'Discovery & Diagnosis',
				description:
					'Understand where you are today and where the friction lives. These services help uncover gaps and define opportunities.',
				icon: Search,
				capabilities: [
					'Initial discovery session',
					'Tech stack assessment and recommendations',
					'Operational bottleneck diagnosis',
					'Organizational readiness assessments',
					'Change management planning',
				],
			},
			{
				name: 'Workflow & Process Design',
				description:
					'Map, analyze, and redesign your internal processes to support better outcomes and readiness for automation.',
				icon: Workflow,
				capabilities: [
					'Current vs. future-state workflow mapping',
					'Standardization and process redesign',
					'Process reengineering with SOPs and specs',
					'Cross-functional requirement facilitation',
					'Automation readiness analysis',
				],
			},
			{
				name: 'Planning & Scoping',
				description:
					'Translate strategy into action with clear plans, task assignments, and aligned resources.',
				icon: Calendar,
				capabilities: [
					'Budget and resource planning',
					'Project execution plans and timelines',
					'Stakeholder analysis and engagement',
					'Requirements documentation',
					'Business case development',
				],
			},
			{
				name: 'Strategic Alignment & KPI Definition',
				description:
					"Connect your operational goals to big-picture outcomes and define how you'll measure success.",
				icon: Target,
				capabilities: [
					'Strategic business analysis',
					'Technology roadmap planning',
					'Digital maturity assessments',
					'KPI and metric definition workshops',
					'Multi-phase strategy engagements',
				],
			},
		],
	},
	{
		id: 'solutions',
		title: 'Systems & Solutions',
		description:
			"Whether you need a simple website, an internal tool, or a full custom platform, we build the systems that run your business day to day, and make sure they keep working as you grow. No tech background required on your end, that's what you're paying us to handle.",
		icon: Code,
		gradient: 'from-green-500 to-teal-500',
		image: '/images/services/automation.png',
		features: [
			{
				name: 'Websites & Portals',
				description:
					'From landing pages to admin portals, we build web solutions that reflect your brand and streamline internal workflows.',
				icon: Globe,
				capabilities: [
					'Landing pages and full marketing websites',
					'Portfolio and blog sites with CMS features',
					'Secure admin portals with custom forms',
					'Client-facing apps with authentication',
				],
			},
			{
				name: 'Custom Business Apps',
				description:
					'Design and develop internal-use or SaaS-grade applications tailored to your business model and team workflows.',
				icon: Layers,
				capabilities: [
					'Power Apps, Dataverse, and SharePoint solutions',
					'Multi-tenant SaaS platforms and web apps',
					'ClickUp, AirTable, and Google Workspace integrations',
					'Form-driven data capture and role-based access',
				],
			},
			{
				name: 'Automation & Workflows',
				description:
					'Replace manual tasks with intelligent automations using Power Automate, Zapier, and cloud-based tools.',
				icon: Zap,
				capabilities: [
					'Multi-step Power Automate or Zapier flows',
					'ClickUp and CRM system automations',
					'Approval processes and data syncing',
					'Internal chatbot workflows and AI assistants',
				],
			},
			{
				name: 'AI & Chatbots',
				description:
					'Add intelligent assistants and AI features to your business tools with customized GPT integrations.',
				icon: Bot,
				capabilities: [
					'Client-facing and internal chatbots',
					'ChatGPT integration into systems',
					'Custom GPTs trained on your data',
					'Role-based prompt control and moderation',
				],
			},
		],
	},
	{
		id: 'analytics',
		title: 'Analytics & Dashboards',
		description:
			"Good data leads to good decisions—but only when it's clean, connected, and clearly presented. We help you get the most out of your data with custom dashboards, automated reporting, and analytics strategies tailored to your business. Whether you're just getting started or scaling up a full data practice, we provide the structure, insights, and enablement to move forward with confidence.",
		icon: LineChart,
		gradient: 'from-purple-500 to-pink-500',
		image: '/images/services/analytics.png',
		features: [
			{
				name: 'Dashboard & Report Design',
				description:
					'Create beautiful, interactive dashboards that turn raw data into stories your team can act on.',
				icon: LayoutDashboard,
				capabilities: [
					'One-page reports and multi-visual dashboards',
					'Executive KPI snapshot views',
					'Compliance and financial reports',
					'Visual refreshes and layout redesigns',
					'Advanced UX features: bookmarks, drill-throughs, tooltip pages',
				],
			},
			{
				name: 'Data Integration & Setup',
				description:
					'Prepare and connect your data from multiple sources so your dashboards stay accurate, fast, and up to date.',
				icon: Database,
				capabilities: [
					'Spreadsheet or legacy data migration',
					'API-based live data pulls',
					'Power BI workspace setup and access roles',
					'Power BI template design for report consistency',
					'Embedded Power BI dashboards in web apps or portals',
				],
			},
			{
				name: 'Automation & Governance',
				description:
					'Automate delivery, protect access, and structure your reporting practice for scale.',
				icon: Shield,
				capabilities: [
					'Recurring report setup and email delivery',
					'User role configuration and data access governance',
					'Power BI workspace organization and naming conventions',
					'Report request tracking system (Forms + Flow)',
					'Data source audits and optimization',
				],
			},
			{
				name: 'Enablement & Strategy',
				description:
					'Support your team in using data more effectively—with training, strategy, and roadmap services that make analytics sustainable.',
				icon: GraduationCap,
				capabilities: [
					'Analytics maturity assessment and roadmap',
					'Analytics enablement playbook',
					'Data literacy training (non-technical audiences welcome)',
					'Embedded analytics strategy for SaaS or service businesses',
				],
			},
		],
	},
	{
		id: 'strategy',
		title: 'Strategy & Support',
		description:
			"We don't just build and walk away. From managing complex tech rollouts to serving as your long-term advisor, we're here to make sure your investments pay off. Think of us as your ongoing partner—always within reach.",
		icon: ClipboardList,
		gradient: 'from-orange-500 to-red-500',
		image: '/images/services/oversight.png',
		features: [
			{
				name: 'Strategic Guidance & Oversight',
				description:
					'Access ongoing leadership and decision-making support from a fractional CTO or project oversight lead.',
				icon: Lightbulb,
				capabilities: [
					'Fractional CTO advisory sessions',
					'Executive IT program oversight',
					'IT budget and resource planning',
					'Portfolio-level milestone tracking',
					'Monthly or quarterly strategy reviews',
				],
			},
			{
				name: 'Training & Adoption',
				description:
					"Help your team actually use the tools and processes you've invested in, with training and documentation built for humans.",
				icon: BookOpen,
				capabilities: [
					'Live training sessions (remote or recorded)',
					'Custom how-to guides and SOPs',
					'Screen recording walkthroughs',
					'Post-launch onboarding support',
					'Process improvement coaching',
				],
			},
			{
				name: 'Governance & Compliance',
				description:
					"Build a strong foundation for how technology is used, secured, and aligned with your organization's goals.",
				icon: FileCheck,
				capabilities: [
					'IT policy development and review',
					'Governance frameworks and playbooks',
					'Compliance audits and reporting',
					'Cybersecurity risk planning',
					'Disaster recovery & business continuity',
				],
			},
			{
				name: 'Optimization & Monitoring',
				description:
					'Maintain momentum with continuous improvement, performance reporting, and operational audits.',
				icon: Activity,
				capabilities: [
					'SaaS and vendor oversight',
					'IT performance monitoring',
					'Service uptime & alerting setup',
					'Tech stack cleanup & tool consolidation',
					'Lean operations and improvement plans',
				],
			},
		],
	},
];
